import React, { useState, useEffect } from 'react';
import { Search, TrendingUp, AlertTriangle, CheckCircle2, BarChart3, Globe, Shield, RefreshCw } from 'lucide-react';
import { toast } from '../components/Toast';
import { Environment } from '../types';
import EmptyState from '../components/EmptyState';

interface SEOProps {
  activeEnv: Environment | null;
}

interface Keyword {
  keyword: string;
  searchVolume: number;
  difficulty: number;
  intent: string;
  recommendation: string;
}

interface Competitor {
  domain: string;
  url: string;
  estimatedTraffic: number;
  domainAuthority: number;
  backlinks: number;
  topKeywords: number;
  organicKeywords: number;
}

interface TechnicalIssue {
  severity: string;
  category: string;
  issue: string;
  recommendation: string;
  affectedPages: number;
}

// Score Ring SVG — Siteimprove-style circular score indicator
const ScoreRing: React.FC<{
  score: number;
  label: string;
  color: string;
  size?: number;
}> = ({ score, label, color, size = 140 }) => {
  const strokeWidth = 10;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;
  const progress = Math.max(0, Math.min(100, score));
  const offset = circumference - (progress / 100) * circumference;

  const getGrade = (s: number) => {
    if (s >= 90) return 'A';
    if (s >= 80) return 'B';
    if (s >= 70) return 'C';
    if (s >= 60) return 'D';
    return 'F';
  };

  return (
    <div className="flex flex-col items-center gap-2">
      <div className="relative" style={{ width: size, height: size }}>
        <svg width={size} height={size} className="transform -rotate-90">
          <circle cx={size / 2} cy={size / 2} r={radius} fill="none" stroke="#e2e8f0" strokeWidth={strokeWidth} />
          <circle
            cx={size / 2} cy={size / 2} r={radius} fill="none"
            stroke={color} strokeWidth={strokeWidth}
            strokeDasharray={circumference} strokeDashoffset={offset}
            strokeLinecap="round" className="transition-all duration-1000 ease-out"
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-3xl font-black text-slate-800">{score}</span>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wide">{getGrade(score)}</span>
        </div>
      </div>
      <span className="text-xs font-bold text-slate-600 text-center">{label}</span>
    </div>
  );
};

const SEO: React.FC<SEOProps> = ({ activeEnv }) => {
  const [activeTab, setActiveTab] = useState<'keywords' | 'competitors' | 'audit'>('keywords');
  const [loading, setLoading] = useState(false);
  const [keywords, setKeywords] = useState<Keyword[]>([]);
  const [competitors, setCompetitors] = useState<{ yourDomain: string; competitors: Competitor[]; insights: string[] } | null>(null);
  const [audit, setAudit] = useState<{ domain: string; score: number; issues: TechnicalIssue[]; recommendations: string[] } | null>(null);
  const [topic, setTopic] = useState('laser energetics');

  // Siteimprove state
  const [siConfigured, setSiConfigured] = useState(false);
  const [siScores, setSiScores] = useState<{ overall: number; seo: number; qa: number; accessibility: number } | null>(null);
  const [siIssues, setSiIssues] = useState<TechnicalIssue[]>([]);
  const [siLoading, setSiLoading] = useState(false);

  // Check if Siteimprove is configured on mount
  useEffect(() => {
    const checkSi = async () => {
      try {
        const resp = await fetch('/api/siteimprove?action=check', { credentials: 'include' });
        if (resp.ok) {
          const data = await resp.json();
          setSiConfigured(data.configured === true);
        }
      } catch { /* not configured */ }
    };
    checkSi();
  }, []);

  const fetchSiteimproveData = async () => {
    setSiLoading(true);
    try {
      const [scoresResp, issuesResp] = await Promise.all([
        fetch('/api/siteimprove?action=scores', { credentials: 'include' }),
        fetch('/api/siteimprove?action=issues', { credentials: 'include' })
      ]);

      if (scoresResp.ok) {
        const data = await scoresResp.json();
        if (data.success) setSiScores(data.scores);
        else toast.error(data.error || 'Failed to fetch Siteimprove scores');
      }

      if (issuesResp.ok) {
        const data = await issuesResp.json();
        if (data.success && data.issues) setSiIssues(data.issues);
      }
    } catch (error) {
      console.error('Siteimprove fetch error:', error);
      toast.error('Failed to connect to Siteimprove');
    } finally {
      setSiLoading(false);
    }
  };

  const runResearch = async (type: 'keywords' | 'competitors' | 'audit') => {
    setLoading(true);
    try {
      const response = await fetch('/api/seo/research', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type,
          topic,
          domain: 'example.com'
        })
      });

      if (response.ok) {
        const data = await response.json();

        if (type === 'keywords') {
          setKeywords(data.data);
        } else if (type === 'competitors') {
          setCompetitors(data.data);
        } else if (type === 'audit') {
          setAudit(data.data);
        }
      } else {
        toast.error('Failed to run SEO research');
      }
    } catch (error) {
      console.error('SEO research error:', error);
      toast.error('Failed to run SEO research');
    } finally {
      setLoading(false);
    }
  };

  if (!activeEnv) return <EmptyState />;

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="border-b border-slate-200 pb-4">
        <h2 className="text-3xl font-bold text-slate-900">SEO Research Tools</h2>
        <p className="text-slate-500 mt-1 text-sm">
          Keyword research, competitive analysis, and technical SEO audits
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 border-b border-slate-200">
        <button
          onClick={() => setActiveTab('keywords')}
          className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${
            activeTab === 'keywords'
              ? 'border-[#003B71] text-[#003B71]'
              : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          <Search size={16} className="inline mr-2" />
          Keyword Research
        </button>
        <button
          onClick={() => setActiveTab('competitors')}
          className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${
            activeTab === 'competitors'
              ? 'border-[#003B71] text-[#003B71]'
              : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          <BarChart3 size={16} className="inline mr-2" />
          Competitor Analysis
        </button>
        <button
          onClick={() => setActiveTab('audit')}
          className={`px-4 py-2 font-bold text-sm border-b-2 transition-colors ${
            activeTab === 'audit'
              ? 'border-[#003B71] text-[#003B71]'
              : 'border-transparent text-slate-400 hover:text-slate-600'
          }`}
        >
          <AlertTriangle size={16} className="inline mr-2" />
          Technical Audit
        </button>
      </div>

      {/* Keyword Research Tab */}
      {activeTab === 'keywords' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <h3 className="font-bold text-lg mb-4">Research Keywords</h3>
            <div className="flex gap-4">
              <input
                type="text"
                value={topic}
                onChange={e => setTopic(e.target.value)}
                className="flex-1 px-4 py-2 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#FFCD00] outline-none"
                placeholder="Enter topic (e.g., laser energetics, inertial fusion)"
              />
              <button
                onClick={() => runResearch('keywords')}
                disabled={loading || activeEnv.isSample}
                className="px-6 py-2 bg-[#003B71] text-white rounded-lg font-bold hover:bg-[#002b51] disabled:opacity-50 transition-colors"
              >
                {loading ? 'Researching...' : 'Research Keywords'}
              </button>
            </div>
          </div>

          {keywords.length > 0 && (
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-600 uppercase">Keyword</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-600 uppercase">Search Volume</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-600 uppercase">Difficulty</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-600 uppercase">Intent</th>
                    <th className="text-left px-6 py-3 text-xs font-bold text-slate-600 uppercase">Recommendation</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {keywords.map((kw, idx) => (
                    <tr key={idx} className="hover:bg-slate-50">
                      <td className="px-6 py-4 font-bold text-sm text-slate-900">{kw.keyword}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{kw.searchVolume.toLocaleString()}/mo</td>
                      <td className="px-6 py-4">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${
                          kw.difficulty < 30 ? 'bg-green-100 text-green-700' :
                          kw.difficulty < 60 ? 'bg-yellow-100 text-yellow-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {kw.difficulty}/100
                        </span>
                      </td>
                      <td className="px-6 py-4 text-xs text-slate-500 uppercase">{kw.intent}</td>
                      <td className="px-6 py-4 text-sm text-slate-600">{kw.recommendation}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Competitor Analysis Tab */}
      {activeTab === 'competitors' && (
        <div className="space-y-6">
          <div className="bg-white rounded-xl border border-slate-200 p-6">
            <button
              onClick={() => runResearch('competitors')}
              disabled={loading || activeEnv.isSample}
              className="px-6 py-2 bg-[#003B71] text-white rounded-lg font-bold hover:bg-[#002b51] disabled:opacity-50 transition-colors"
            >
              {loading ? 'Analyzing...' : 'Analyze Competitors'}
            </button>
          </div>

          {competitors && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {competitors.competitors.map((comp, idx) => (
                  <div key={idx} className="bg-white rounded-xl border border-slate-200 p-6">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="font-bold text-sm text-slate-900">{comp.domain}</h4>
                      <Globe size={16} className="text-slate-400" />
                    </div>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between">
                        <span className="text-slate-500">Est. Traffic:</span>
                        <span className="font-bold">{comp.estimatedTraffic.toLocaleString()}/mo</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Domain Authority:</span>
                        <span className="font-bold">{comp.domainAuthority}/100</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Backlinks:</span>
                        <span className="font-bold">{comp.backlinks.toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-slate-500">Organic Keywords:</span>
                        <span className="font-bold">{comp.organicKeywords.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                <h4 className="font-bold text-blue-900 mb-3">Key Insights</h4>
                <ul className="space-y-2">
                  {competitors.insights.map((insight, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-blue-800">
                      <TrendingUp size={16} className="mt-0.5 flex-shrink-0" />
                      {insight}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}

      {/* Technical Audit Tab */}
      {activeTab === 'audit' && (
        <div className="space-y-6">
          {/* Action buttons */}
          <div className="bg-white rounded-xl border border-slate-200 p-6 flex flex-wrap items-center gap-3">
            {siConfigured && (
              <button
                onClick={fetchSiteimproveData}
                disabled={siLoading}
                className="px-6 py-2 bg-indigo-600 text-white rounded-lg font-bold hover:bg-indigo-700 disabled:opacity-50 transition-colors flex items-center gap-2"
              >
                <Shield size={16} />
                {siLoading ? 'Loading...' : 'Pull Siteimprove Scores'}
              </button>
            )}
            <button
              onClick={() => runResearch('audit')}
              disabled={loading || activeEnv.isSample}
              className="px-6 py-2 bg-[#003B71] text-white rounded-lg font-bold hover:bg-[#002b51] disabled:opacity-50 transition-colors"
            >
              {loading ? 'Auditing...' : 'Run AI Audit'}
            </button>
            {!siConfigured && (
              <span className="text-xs text-slate-400">
                Add SITEIMPROVE_EMAIL, SITEIMPROVE_API_KEY, SITEIMPROVE_SITE_ID to Cloudflare secrets for live scores
              </span>
            )}
          </div>

          {/* Siteimprove Score Rings (real data) */}
          {siScores && (
            <div className="bg-white rounded-xl border-2 border-indigo-200 p-8">
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center gap-2">
                  <Shield size={18} className="text-indigo-600" />
                  <h3 className="font-bold text-lg">Siteimprove Digital Certainty Index</h3>
                  <span className="text-[10px] font-bold text-indigo-600 bg-indigo-50 px-2 py-0.5 rounded-full uppercase">Live</span>
                </div>
                <button
                  onClick={fetchSiteimproveData}
                  disabled={siLoading}
                  className="p-2 text-slate-400 hover:text-indigo-600 transition-colors"
                  title="Refresh scores"
                >
                  <RefreshCw size={16} className={siLoading ? 'animate-spin' : ''} />
                </button>
              </div>
              <div className="flex items-center justify-center gap-12 flex-wrap">
                <ScoreRing
                  score={siScores.overall}
                  label="Overall DCI"
                  color={siScores.overall >= 80 ? '#16a34a' : siScores.overall >= 60 ? '#d97706' : '#dc2626'}
                  size={160}
                />
                <div className="w-px h-24 bg-slate-200 hidden md:block" />
                <ScoreRing score={siScores.seo} label="SEO" color="#2563eb" />
                <ScoreRing score={siScores.qa} label="Quality Assurance" color="#7c3aed" />
                <ScoreRing score={siScores.accessibility} label="Accessibility" color="#0891b2" />
              </div>
            </div>
          )}

          {/* Siteimprove Issues */}
          {siIssues.length > 0 && (
            <div className="bg-white rounded-xl border-2 border-indigo-200 overflow-hidden">
              <div className="px-6 py-4 bg-indigo-50 border-b border-indigo-200 flex items-center gap-2">
                <Shield size={16} className="text-indigo-600" />
                <h4 className="font-bold text-slate-900">Siteimprove Issues</h4>
                <span className="text-xs text-slate-500 ml-2">({siIssues.length} issues affecting your scores)</span>
              </div>
              <div className="divide-y divide-slate-100">
                {siIssues.map((issue, idx) => (
                  <div key={`si-${idx}`} className="p-6">
                    <div className="flex items-start gap-4">
                      <div className={`p-2 rounded-lg ${
                        issue.severity === 'high' || issue.severity === 'error' ? 'bg-red-100 text-red-600' :
                        issue.severity === 'medium' || issue.severity === 'warning' ? 'bg-yellow-100 text-yellow-600' :
                        'bg-blue-100 text-blue-600'
                      }`}>
                        <AlertTriangle size={20} />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h5 className="font-bold text-sm text-slate-900">{issue.issue}</h5>
                          {issue.affectedPages > 0 && (
                            <span className="text-xs text-slate-500">({issue.affectedPages} pages)</span>
                          )}
                        </div>
                        {issue.recommendation && (
                          <p className="text-sm text-slate-600 mb-2">{issue.recommendation}</p>
                        )}
                        <span className="text-xs font-bold text-indigo-400 uppercase">{issue.category}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* AI Audit Results */}
          {audit && (
            <>
              <div className="bg-white rounded-xl border border-slate-200 p-6">
                <div className="flex items-center justify-between mb-6">
                  <div className="flex items-center gap-2">
                    <h3 className="font-bold text-lg">Overall SEO Health</h3>
                    <span className="text-[10px] font-bold text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full uppercase">AI Estimated</span>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-black text-[#003B71]">{audit.score}/100</div>
                    <div className="text-xs text-slate-500">Technical Score</div>
                  </div>
                </div>
              </div>

              <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                <div className="px-6 py-4 bg-slate-50 border-b border-slate-200">
                  <h4 className="font-bold text-slate-900">Issues Found</h4>
                </div>
                <div className="divide-y divide-slate-100">
                  {audit.issues.map((issue, idx) => (
                    <div key={idx} className="p-6">
                      <div className="flex items-start gap-4">
                        <div className={`p-2 rounded-lg ${
                          issue.severity === 'high' ? 'bg-red-100 text-red-600' :
                          issue.severity === 'medium' ? 'bg-yellow-100 text-yellow-600' :
                          'bg-blue-100 text-blue-600'
                        }`}>
                          <AlertTriangle size={20} />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <h5 className="font-bold text-sm text-slate-900">{issue.issue}</h5>
                            <span className="text-xs text-slate-500">({issue.affectedPages} pages)</span>
                          </div>
                          <p className="text-sm text-slate-600 mb-2">{issue.recommendation}</p>
                          <span className="text-xs font-bold text-slate-400 uppercase">{issue.category}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-6">
                <h4 className="font-bold text-green-900 mb-3">Recommendations</h4>
                <ul className="space-y-2">
                  {audit.recommendations.map((rec, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-green-800">
                      <CheckCircle2 size={16} className="mt-0.5 flex-shrink-0" />
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default SEO;
